import { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PageLayout, Divider, ParticipantsList } from '../components';
import { PHOTOS, PARTICIPANT_OFFSET, VOTES_OFFSET } from '../constants';
import { useLanguage } from '../contexts/LanguageContext';
import { useRegisterModal } from '../contexts/RegisterModalContext';
import { getTgBotUser } from '../services/firestore';
import { resolveImageUrl } from '../services/firebaseStorage';

export default function VotePage() {
  const { t } = useLanguage();
  const { openRegister } = useRegisterModal();
  const location = useLocation();
  
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [participants, setParticipants] = useState([]);

  const slug = location.pathname.slice(1);

  useEffect(() => {
    const knownRoutes = ['', 'models', 'actresses', 'dancers', 'sessions'];
    if (knownRoutes.includes(slug)) {
      setLoading(false);
      return;
    }

    async function fetchUser() {
      setLoading(true);
      setNotFound(false);
      try {
        console.log('[Запрос к Firestore] getTgBotUser:', slug);
        const data = await getTgBotUser(slug);
        console.log('[Ответ Firestore] getTgBotUser:', data);
        if (data) {
          setUserData(data);
        } else {
          setNotFound(true);
        }
      } catch (e) {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchUser();
  }, [slug]);

  // Поддержать старую и новую структуру ответа:
  // старая: userData.last_referal, новая: userData.data
  const contestData = userData?.data ?? userData?.last_referal ?? userData;

  // Разрешаем изображения асинхронно и сохраняем участников в state
  useEffect(() => {
    let mounted = true;
    async function buildParticipants() {
      console.debug('[VotePage] buildParticipants, contestData:', contestData);
      if (!contestData?.options) {
        console.debug('[VotePage] нет опций в contestData');
        if (mounted) setParticipants([]);
        return;
      }

      const entries = Object.entries(contestData.options);
      const list = await Promise.all(entries.map(async ([key, opt], index) => {
        try {
          console.debug(`[VotePage] resolving image for option ${key}`, opt);
          const resolved = await resolveImageUrl(opt).catch((e) => {
            console.warn(`[VotePage] resolveImageUrl failed for ${key}:`, e && e.message ? e.message : e);
            return null;
          });
          console.debug(`[VotePage] resolved image for ${key}:`, resolved);

          return {
            id: `participant-${key}`,
            name: opt.name || `Участница ${key}`,
            age: opt.age || 0,
            description: opt.description || '',
            votes: (opt.votes),
            image: resolved || opt.photo_firebase_uri || opt.photo || PHOTOS[index % PHOTOS.length],
            nominationId: Array.isArray(contestData.nominations) ? (contestData.nominations[0] || 'default') : (contestData.nominations || 'default'),
            rawOption: opt,
          };
        } catch (e) {
          console.error(`[VotePage] Ошибка при обработке опции ${key}:`, e && e.message ? e.message : e);
          return null;
        }
      }));

      const filtered = list.filter(Boolean);
      console.debug('[VotePage] participants resolved count:', filtered.length, filtered.map(p => ({ id: p.id, image: p.image })));
      if (mounted) setParticipants(filtered);
    }

    buildParticipants();
    return () => { mounted = false; };
  }, [contestData]);

  const handleVote = useCallback(() => {
    openRegister('Чтобы проголосовать, нужно зарегистрироваться');
  }, [openRegister]);

  if (loading) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] anim-fade-up">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-sky-500 rounded-full animate-spin" />
          <p className="mt-4 text-slate-600 font-medium">Загрузка...</p>
        </div>
      </PageLayout>
    );
  }

  if (notFound) {
    return (
      <PageLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center anim-fade-up">
          <div className="w-24 h-24 rounded-full bg-slate-100 flex items-center justify-center mb-6">
            <svg className="w-12 h-12 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl xs:text-3xl font-bold text-slate-800 mb-3">Страница не найдена</h1>
          <p className="text-slate-600 text-base max-w-md mb-6">
            К сожалению, страница <span className="font-semibold text-slate-800">/{slug}</span> не существует или была удалена.
          </p>
          <a href="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold shadow-lg hover:shadow-xl transition-shadow">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            На главную
          </a>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <section className="flex flex-col justify-center items-center py-8 xs:py-12 anim-fade-up">
        <div className="mb-4 xs:mb-6 px-4 xs:px-6 w-full flex flex-col items-center">
          {contestData?.total_voters && (
            <div
              className="mb-2 xs:mb-4 px-5 py-3 xs:px-7 xs:py-4 rounded-2xl bg-gradient-to-r from-pink-500 via-sky-500 to-blue-600 shadow-lg text-white text-lg xs:text-2xl font-extrabold tracking-tight border border-white/30"
              style={{ letterSpacing: '0.02em' }}
            >
                <span style={{ textShadow: '0 2px 8px rgba(0,0,0,0.18)' }}>
                {(contestData.total_voters) } человек сейчас голосуют с вами
              </span>
            </div>
          )}
          <div className="flex flex-col items-center">
            <p className="text-sm xs:text-base 550:text-xl tablet:text-2xl font-black tracking-tight text-slate-700 mb-1">
              {t('vote.nominations')}
              {contestData?.nominations && Array.isArray(contestData.nominations) && contestData.nominations.length > 0 && (
                <span className="ml-2 font-semibold text-sky-700">{contestData.nominations.join(', ')}</span>
              )}
            </p>
          </div>
        </div>
        <Divider gradient className="mt-2 xs:mt-4 anim-fade-in" style={{ animationDelay: '80ms' }} />
        <div className="flex flex-col mt-4 xs:mt-6 w-fit mr-auto px-4 xs:px-6 pb-2 anim-fade-up" style={{ animationDelay: '120ms' }}>
          <h1 className="text-xl xs:text-2xl 550:text-3xl tablet:text-4xl font-black tracking-tight leading-tight bg-gradient-to-r from-sky-600 via-blue-600 to-sky-600 text-transparent bg-clip-text">
            {contestData?.title || t('vote.title')}
          </h1>
          <p className="mt-2 xs:mt-4 text-sm xs:text-base font-medium leading-5 text-slate-600">
            {contestData?.description || t('vote.subtitle')}
          </p>
        </div>
      </section>

      <section className="flex flex-col gap-3 xs:gap-4 pb-4 xs:pb-6 anim-fade-up" style={{ animationDelay: '160ms' }}>
        <div className="mx-3 xs:mx-6 rounded-xl xs:rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 px-3 xs:px-4 py-2 xs:py-3 text-white text-sm xs:text-base font-semibold shadow-sm ring-1 ring-white/20 anim-fade-in" style={{ animationDelay: '180ms' }}>
          {t('vote.banner')}
        </div>

        <ParticipantsList 
          participants={participants} 
          onVote={handleVote}
          blurImages={false}
        />
      </section>
    </PageLayout>
  );
}
